/**
 * Echo Hunt -- model reasoning capabilities (2026-10-21).
 *
 * ## The problem this solves
 *
 * The shared LLM helper (`Utils/AI/LLMs/@Shared.ts`) defaults `thinkingType` to DISABLED, which
 * `GenerateNextMessage_OpenRouter` turns into `reasoning: {effort: "none"}`. Some endpoints reject that
 * outright:
 *
 *     BadRequestResponseError: Reasoning is mandatory for this endpoint and cannot be disabled.
 *
 * (`anthropic/claude-opus-5.5` is one: OpenRouter's model metadata reports
 * `reasoning: {mandatory: true, supported_efforts: [...], default_effort: "high"}`.)
 *
 * So before an Echo Hunt call, the model's metadata is consulted. If reasoning is mandatory, an effort is
 * chosen from what the model actually supports; otherwise nothing changes, so the existing behaviour (and
 * cost/latency) is untouched for every model that works today.
 *
 * ## Why the parsing lives here and the fetch does not
 *
 * Everything in this file is pure so it can be unit-tested in the Node lane: the metadata parsing, the effort
 * choice, and the compatibility check all operate on a plain object. `ModelCapabilities_Live.ts` is the thin
 * wrapper that fetches and caches from OpenRouter.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §16.0 (LLM execution).
 */
import {ReasoningEffort, ReasoningEffort_values} from "./Roles.js";

/** The parts of OpenRouter's model metadata this feature reads. Everything is optional: a provider that
 * reports nothing is treated as "no reasoning information", never as an error. */
export type ModelCapabilities = {
	/** the model id this was fetched for, so a cache cannot return the wrong entry */
	model: string;
	/** does this endpoint refuse a request that disables reasoning? */
	reasoningMandatory: boolean;
	/** efforts the model accepts, in the provider's own order. Empty = unknown. */
	supportedEfforts: ReasoningEffort[];
	/** what the provider uses when no effort is given. Null = unknown. */
	defaultEffort: ReasoningEffort|null;
	/** does the model accept a `reasoning` parameter at all? */
	supportsReasoning: boolean;
};

/** The capabilities assumed when the metadata could not be fetched. Deliberately CONSERVATIVE in the
 * direction that cannot break a request: "no reasoning information" means the call is made exactly as it
 * would have been without this feature, so a network failure degrades to the old behaviour rather than to a
 * new error. */
export function UnknownCapabilities(model: string): ModelCapabilities {
	return {
		model,
		reasoningMandatory: false,
		supportedEfforts: [],
		defaultEffort: null,
		supportsReasoning: false,
	};
}

function IsReasoningEffort(value: unknown): value is ReasoningEffort {
	return typeof value == "string" && (ReasoningEffort_values as readonly string[]).includes(value);
}

/** Extracts `ModelCapabilities` from an OpenRouter `/api/v1/model/{id}` response body.
 *
 * Tolerant by design: the response wraps the model in `{data: {...}}`, but a bare object is accepted too, and
 * an unrecognised effort name is DROPPED rather than guessed at (guessing "xhigh" for an unknown value could
 * produce the very BadRequest this feature exists to avoid). `supported_parameters` is where the parameter
 * support lives; `reasoning` is where the mandatory/supported-efforts information lives. */
export function ParseModelCapabilities(model: string, body: unknown): ModelCapabilities {
	const root = body as {data?: unknown}|n;
	const data = (root?.data ?? body) as {
		supported_parameters?: unknown,
		reasoning?: unknown,
	}|n;

	const params = Array.isArray(data?.supported_parameters)
		? data!.supported_parameters.filter((a): a is string=>typeof a == "string")
		: [];
	const supportsReasoning = params.includes("reasoning") || params.includes("reasoning_effort");

	const reasoning = data?.reasoning as {
		mandatory?: unknown,
		supported_efforts?: unknown,
		default_effort?: unknown,
	}|n;

	const supportedEfforts = Array.isArray(reasoning?.supported_efforts)
		? reasoning!.supported_efforts.filter(IsReasoningEffort)
		: [];

	return {
		model,
		// a model that reports supported efforts but no `mandatory` flag is NOT assumed mandatory: being wrong in
		// that direction costs an unnecessary reasoning budget on every call, which is the more expensive error
		reasoningMandatory: reasoning?.mandatory === true,
		supportedEfforts,
		defaultEffort: IsReasoningEffort(reasoning?.default_effort) ? reasoning!.default_effort as ReasoningEffort : null,
		supportsReasoning: supportsReasoning || supportedEfforts.length > 0 || reasoning?.mandatory === true,
	};
}

export type ResolveReasoningOptions = {
	/** the user's per-role choice; null/absent = "auto" */
	requested?: ReasoningEffort|n;
};

export type ResolvedReasoning = {
	/** does the call need an explicit effort at all? False = leave the shared helper's default alone */
	reasoningRequired: boolean;
	/** the effort to request; null when `reasoningRequired` is false */
	effort: ReasoningEffort|null;
	/** a short explanation for the UI/log, e.g. "mandatory; using the model's default (high)" */
	note: string;
};

/** Decides whether (and how) to request reasoning for one call.
 *
 * Rules, in order:
 *  1. The user's explicit per-role choice wins, as long as the model supports it. An unsupported choice is
 *     DOWNGRADED to the nearest supported effort rather than sent (which would be a `BadRequest`), and the
 *     note says so.
 *  2. No choice and reasoning is NOT mandatory: nothing changes. This is what keeps every currently-working
 *     model on its current cost/latency.
 *  3. No choice and reasoning IS mandatory: the model's own default effort, else the first supported effort,
 *     else `high` (the value OpenRouter reports as the usual default and the one `EffortLevel_ForOpenRouter`
 *     already maps cleanly).
 */
export function ResolveReasoningEffort(capabilities: ModelCapabilities, options?: ResolveReasoningOptions): ResolvedReasoning {
	const requested = options?.requested ?? null;
	const supported = capabilities.supportedEfforts;

	if (requested != null) {
		// "none" means "leave reasoning off", which is the shared helper's default rather than a value to send.
		// (The OpenRouter helper even asserts against sending effort=none together with reasoning enabled.)
		if (requested == "none" && !capabilities.reasoningMandatory) {
			return {reasoningRequired: false, effort: null, note: "reasoning is off, as you chose"};
		}
		if (supported.length == 0 || supported.includes(requested)) {
			return {reasoningRequired: true, effort: requested, note: `using your chosen effort (${requested})`};
		}
		const nearest = NearestSupportedEffort(requested, supported);
		return {
			reasoningRequired: true,
			effort: nearest,
			note: `"${requested}" is not supported by this model; using the nearest supported effort (${nearest})`,
		};
	}

	if (!capabilities.reasoningMandatory) {
		return {
			reasoningRequired: false,
			effort: null,
			note: capabilities.supportsReasoning
				? "reasoning is optional for this model, so the default (disabled) is kept"
				: "this model reports no reasoning support, so nothing is sent",
		};
	}

	// `supported[0]` is skipped when it is "none": a mandatory-reasoning endpoint that lists "none" would still
	// reject the request, so the fallback goes to a real effort
	const effort = capabilities.defaultEffort
		?? supported.find(a=>a != "none")
		?? "high";
	return {
		reasoningRequired: true,
		effort,
		note: capabilities.defaultEffort != null
			? `reasoning is mandatory for this model; using its default effort (${effort})`
			: `reasoning is mandatory for this model; using ${effort}`,
	};
}

/** The supported effort closest to `wanted` in the canonical ordering. Ties resolve DOWNWARD (to the cheaper
 * effort), because an over-provisioned reasoning budget is a cost the user did not ask for. */
export function NearestSupportedEffort(wanted: ReasoningEffort, supported: ReasoningEffort[]): ReasoningEffort {
	if (supported.length == 0) return wanted;
	const wantedIndex = ReasoningEffort_values.indexOf(wanted);

	let best = supported[0];
	let bestDistance = Number.POSITIVE_INFINITY;
	for (const candidate of supported) {
		const distance = Math.abs(ReasoningEffort_values.indexOf(candidate) - wantedIndex);
		if (distance < bestDistance) {
			best = candidate;
			bestDistance = distance;
		}
	}
	return best;
}

/** The per-role choices to offer in the UI, for one model: "auto" plus whatever the model supports, in the
 * canonical order. A model that reports nothing gets "auto" plus the full list, so a user can still set an
 * effort for a model whose metadata could not be read. */
export function ReasoningChoicesFor(capabilities: ModelCapabilities|null): (ReasoningEffort|null)[] {
	const supported = capabilities?.supportedEfforts ?? [];
	if (supported.length == 0) return [null, ...ReasoningEffort_values];
	return [null, ...ReasoningEffort_values.filter(a=>supported.includes(a))];
}

/** True when an error message is the "reasoning is mandatory" rejection. Used to fall back cleanly on an API
 * type whose metadata is unavailable (eg. Anthropic-direct, which has no per-model endpoint to consult). */
export function IsReasoningMandatoryError(message: string|n): boolean {
	if (message == null) return false;
	return /reasoning is mandatory/i.test(message);
}
